import React, { useState } from "react";
import { Download, FileText, Lock } from "lucide-react";
import axios from "axios";
import { useAuth } from "../../scripts/AuthContext";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastError } from "@/lib/toast";
import { isPostAttachmentPreviewable } from "@/lib/postAttachments";
import DmImageAttachment from "./DmImageAttachment";
import TeamFilePreviewDialog from "@/components/TeamFilePreviewDialog";
import { cn } from "@/lib/utils";

function isImageMime(mime) {
  return String(mime || "").startsWith("image/");
}

function fileItemsFromAttachment(attachment) {
  if (!attachment) return [];
  if (attachment.type === "image") {
    return [
      {
        ...attachment,
        type: "file",
        mime_type: attachment.mime_type || "image/png",
      },
    ];
  }
  if (attachment.type === "attachments" && Array.isArray(attachment.items)) {
    return attachment.items;
  }
  return [];
}

function localPreviewItem(item) {
  return {
    ...item,
    name: item.file_name || item.name || "File",
    previewable: isPostAttachmentPreviewable(item),
  };
}

function LocalFileChip({ item, orgId, onOpen }) {
  const { accessToken } = useAuth();
  const name = item.file_name || "File";
  const image = isImageMime(item.mime_type);
  const previewable = isPostAttachmentPreviewable(item);

  const download = async () => {
    if (!item.url || !accessToken) return;
    try {
      const res = await axios.get(`${API_BASE_URL}${item.url}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: orgId ? { org_id: orgId } : undefined,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err, "Download failed");
    }
  };

  const openPreview = () => onOpen(localPreviewItem(item));

  if (image) {
    return (
      <button
        type="button"
        onClick={openPreview}
        className="mt-1 max-w-[280px] rounded-md text-left hover:opacity-90"
        title="Preview image"
      >
        <DmImageAttachment
          attachment={{ ...item, type: "image" }}
          orgId={orgId}
        />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={previewable ? openPreview : download}
      className="mt-1 inline-flex max-w-full items-center gap-2 rounded-md border border-border bg-muted/40 px-2 py-1.5 text-left text-xs hover:bg-muted"
    >
      <FileText className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{name}</span>
      {!previewable ? (
        <Download className="h-3 w-3 shrink-0 text-muted-foreground" />
      ) : null}
    </button>
  );
}

function TeamFileChip({ item, orgId, onOpen }) {
  if (item.accessible === false) {
    return (
      <span className="mt-1 inline-flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2 py-1.5 text-xs text-muted-foreground">
        <Lock className="h-3.5 w-3.5 shrink-0" />
        Unavailable
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className={cn(
        "mt-1 inline-flex max-w-full items-center gap-2 rounded-md border border-border bg-muted/40 px-2 py-1.5 text-left text-xs hover:bg-muted"
      )}
    >
      <FileText className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{item.name || "Team file"}</span>
    </button>
  );
}

/**
 * Renders workplace DM image / multi-file / Team File attachments.
 */
export default function DmFileAttachments({ attachment, orgId }) {
  const { accessToken } = useAuth();
  const [preview, setPreview] = useState(null);
  const items = fileItemsFromAttachment(attachment);
  if (!items.length) return null;

  return (
    <div className="flex flex-col items-stretch gap-0.5">
      {items.map((item, idx) => {
        if (item.type === "team_file") {
          return (
            <TeamFileChip
              key={`tf-${item.team_id}-${item.node_id}-${idx}`}
              item={item}
              orgId={orgId}
              onOpen={setPreview}
            />
          );
        }
        return (
          <LocalFileChip
            key={`f-${item.attachment_id || idx}`}
            item={item}
            orgId={orgId}
            onOpen={setPreview}
          />
        );
      })}
      <TeamFilePreviewDialog
        open={!!preview}
        onOpenChange={(o) => {
          if (!o) setPreview(null);
        }}
        orgId={orgId}
        teamId={preview?.team_id}
        item={preview}
        accessToken={accessToken}
      />
    </div>
  );
}
